import heroImage from '../../../assets/images/courier-boxes.webp';
import { CANNOT_SHIP } from './shared';
import type { ServiceContent } from './types';

const packing: ServiceContent = {
  slug: 'packing-and-shipping',
  breadcrumbName: 'Packing and Shipping',
  meta: {
    title: 'Cargo Packing & Shipping Service in Saudi Arabia | Sahari Cargo',
    description:
      'Professional cargo packing in Saudi Arabia — sturdy boxes, wrapping and strapping, labelled and ready to ship by air or sea. Book packing with your shipment.',
  },
  heroImage,
  heroAlt: 'Cargo boxes being packed for shipping',
  h1: 'Packing and Shipping Service',
  intro: [
    "Most damage in international cargo doesn't happen in the air or at sea. It happens because a box wasn't packed for the journey ahead — loaded, unloaded, stacked, and moved through customs several times before it arrives. Our team packs your shipment for that whole journey, then ships it by air or sea, so the packing and the shipping are handled by the same people.",
  ],
  sections: [
    {
      heading: 'Why Packing Matters for International Cargo',
      paragraphs: [
        'A cargo box going abroad gets handled far more than a parcel sent across town. It sits under other boxes, gets moved by forklift, and gets opened and resealed at customs if it is inspected. A box that is too weak, half-empty, or badly taped is the one that arrives crushed. Good packing is the cheapest insurance your shipment can have.',
      ],
    },
    {
      heading: 'How We Pack Your Shipment',
      steps: [
        { title: 'The right box', text: 'We choose a strong box sized to the contents, so nothing shifts around inside.' },
        { title: 'Wrap and protect', text: 'Items are wrapped and gaps are filled, with extra protection for anything fragile.' },
        { title: 'Seal and strap', text: 'The box is taped and strapped so it holds its shape under the weight of other cargo.' },
        { title: 'Photographed', text: 'We photograph the packed box, so there is a record of how it left us.' },
        { title: 'Labelled and numbered', text: 'Every box gets its own number and label, so it can be tracked individually to the destination.' },
      ],
    },
    {
      heading: 'What We Pack',
      chips: [
        'Household items',
        'Clothes & personal belongings',
        'Fragile items',
        'Electronics',
        'Documents',
        'Gifts for family',
        'Commercial goods',
      ],
      warning: CANNOT_SHIP,
    },
    {
      heading: "Packing Tips If You're Packing Yourself",
      paragraphs: [
        "You're welcome to pack your own boxes. If you do, these few things make the biggest difference:",
      ],
      bullets: [
        'Use a new, double-wall box — old boxes lose most of their strength.',
        'Fill every gap so the contents cannot move when the box is shaken.',
        'Put heavy items at the bottom and keep each box to a weight one person can lift.',
        'Wrap fragile items individually and keep them away from the sides of the box.',
        'Tape every seam, not just the middle — top and bottom.',
        'Keep a list of what is in each box; you will need it for the customs declaration.',
      ],
    },
    {
      heading: 'Why Choose Sahari Cargo for Packing',
      paragraphs: [
        'Because the people who pack your boxes are the same people who ship them. Our team knows what a box goes through between Saudi Arabia and the destination, packs for it, and keeps a photo and a box number on record for every one — so if you ever need to ask about a box, we know exactly which one you mean.',
      ],
    },
  ],
  faqs: [
    {
      q: 'Can you pack my items when you collect them?',
      a: 'Yes. When we collect from your address, our team can pack your items on the spot. You can also bring them to your nearest Sahari Cargo branch to be packed.',
    },
    {
      q: 'Do you pack fragile items?',
      a: 'Yes. Fragile items are wrapped individually, cushioned, and kept away from the sides of the box. Let us know when booking so we can prepare the right materials.',
    },
    {
      q: 'Can I pack my boxes myself?',
      a: 'Yes. You can bring your own packed boxes. We check that they are sealed properly before shipping, and every box still gets its own number and label.',
    },
    {
      q: 'How will I know which box is which?',
      a: 'Every box gets its own number and label when it is packed, so each one can be tracked individually through to delivery.',
    },
    {
      q: 'Can packed boxes go by both air and sea?',
      a: 'Yes. Once packed, your boxes can ship by air cargo or sea freight, depending on the service you choose.',
    },
  ],
  cta: {
    title: 'Want it packed and shipped?',
    text: "Tell us what you're sending and where, and we'll take care of the packing and the shipping.",
  },
};

export default packing;
